import {
	MzcrContext,
	useGetMzcrContextQuery,
	useGetPatientRidQuery,
	useLookupPatientRidMutation,
} from './mzcrApi';
import useMzcrAccess from './useMzcrAccess';

/** What the MZČR tab of the patient card shows (design "Stavy služeb MZČR v kartě pacienta"). */
export type MzcrPatientState =
	| { status: 'loading' }
	/** Only the setup ACL - the workplace has no MZČR certificate yet. */
	| { status: 'noCertificate' }
	| { status: 'error' }
	| { status: 'ridMissingData' }
	| { status: 'ridNotFound' }
	/** RID not stored in the profile yet - the user starts the registry lookup. */
	| { status: 'ridNotPaired' }
	| { status: 'ready'; rid: string; context: MzcrContext };

/**
 * State of the MZČR services for one patient: the context of the workplace and the patient's RID.
 * Both come from the RTK Query cache, so more tabs of the patient card (and a retry) share them.
 */
export default function useMzcrPatientService(patientId: string) {
	const { canSetup, hasServices } = useMzcrAccess();
	// The registry cannot be called without the certificate - nothing to load, the user sets it up
	const noCertificate = canSetup && !hasServices;

	const context = useGetMzcrContextQuery(undefined, { skip: noCertificate });
	const storedRid = useGetPatientRidQuery(patientId, { skip: noCertificate });
	const [lookup, lookupResult] = useLookupPatientRidMutation({
		fixedCacheKey: `mzcr-rid-${patientId}`,
	});

	const lookupRid = () => {
		lookup(patientId);
	};

	const retry = () => {
		if (context.isError) {
			context.refetch();
		}
		if (storedRid.isError) {
			storedRid.refetch();
		}
		if (lookupResult.isError) {
			lookupRid();
		}
	};

	const lookedUp = lookupResult.data;
	const rid =
		storedRid.data?.rid ?? (lookedUp?.found ? lookedUp.rid : undefined);

	let state: MzcrPatientState;
	if (noCertificate) {
		state = { status: 'noCertificate' };
	} else if (
		context.isLoading ||
		context.isUninitialized ||
		storedRid.isLoading ||
		storedRid.isUninitialized
	) {
		state = { status: 'loading' };
	} else if (context.isError || storedRid.isError || lookupResult.isError) {
		state = { status: 'error' };
	} else if (rid && context.data) {
		state = { status: 'ready', rid, context: context.data };
	} else if (lookedUp && !lookedUp.found) {
		state =
			lookedUp.reason === 'MISSING_INPUT_DATA'
				? { status: 'ridMissingData' }
				: { status: 'ridNotFound' };
	} else {
		state = { status: 'ridNotPaired' };
	}

	return {
		state,
		/** Something is being (re)loaded - for the loading state of the action buttons. */
		isBusy:
			context.isFetching || storedRid.isFetching || lookupResult.isLoading,
		lookupRid,
		retry,
	};
}
